/**
 * Caches whether the server can wake the paired phone out-of-band.
 *
 * The answer decides whether the PC offers reply and notification-action controls at all: a
 * phone with no push route cannot act on them until it is next opened, and a reply that lands
 * after the conversation moved on is worse than no reply control. See
 * `docs/superpowers/specs/2026-08-07-android-push-fallback-design.md` D2/D4.
 *
 * The web twin of the extension's `refreshPushCapability` in `background/session.ts`.
 */

import { webStorage } from "../adapters/storage.web";
import { fetchDeviceDiagnostics } from "../api/client";
import { logWarn } from "../lib/log";

/**
 * Refresh `phonePushCapable` from the server.
 *
 * Fire-and-forget — callers must not await it. It runs on app open, and the page reads the
 * cached value reactively, so a late write still lands without holding up first paint.
 *
 * A failure leaves the previous value in place rather than writing `false`. Downgrading on a
 * transient error would strip working reply controls from a healthy pairing, which is the wrong
 * direction to fail in; absent stays absent and reads as "unknown", which is treated as capable.
 */
export async function refreshPushCapability(): Promise<void> {
  try {
    const { token, androidDeviceId } = await webStorage.get("token", "androidDeviceId");
    if (!token || !androidDeviceId) return;
    const snapshot = await fetchDeviceDiagnostics(token, androidDeviceId);
    await webStorage.set({ phonePushCapable: snapshot.partnerPushCapable });
  } catch (err) {
    // Never fatal — the page keeps whatever it had.
    logWarn("notifications", "push capability refresh failed", err);
  }
}
